import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import { Button, Tag, Title, TitleProps } from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import { useCarouselButtons } from '../../../hooks/useCarouselButtons';
import DynamicCard, { DynamicCardProps } from '../../molecules/DynamicCard';

export interface CarouselSectionProps {
    items?: DynamicCardProps[];
    variant: 'testimonial' | 'mediaLarge';
    title?: string;
    filters?: { label: string; value: string }[];
    titleTag?: TitleProps['component'];
    onClickFilter?: (tag: string) => void;
    filterActive?: string | null;
}

export default function CarouselSection({
    items = [],
    variant,
    title,
    titleTag,
    filters,
    filterActive,
    onClickFilter,
}: CarouselSectionProps) {
    const handleTagClick = (tag: string) => {
        typeof onClickFilter === 'function' && onClickFilter(tag);

        emblaApi?.scrollTo(0);
    };

    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'center',
        active: items?.length > 1 ? true : false,
    });

    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    } = useCarouselButtons(emblaApi);

    return (
        <section className="my-xxxl overflow-hidden">
            <div
                className={cls(
                    'container flex flex-col justify-between',
                    variant === 'testimonial' && 'md:flex-row md:items-end',
                    variant === 'mediaLarge' && 'lg:flex-row lg:items-end',
                )}>
                {title && (
                    <Title
                        isHtml
                        component={titleTag ?? 'h3'}
                        className={cls(
                            'flex-1 text-h300 text-text-primary',
                            variant === 'testimonial' &&
                                ' md:max-w-[62.5%] lg:md:max-w-[37.5%] xl:md:max-w-[42%]',
                            variant === 'mediaLarge' && '',
                        )}>
                        {title}
                    </Title>
                )}
                {filters && filters.length > 0 && (
                    <div className="mt-24 lg:mt-0 pr-32 flex gap-8">
                        {filters.map(({ label, value }) => (
                            <Tag
                                key={`filter-${label}`}
                                variant="filter"
                                onClick={() => handleTagClick(value)}
                                active={filterActive === value}>
                                {label}
                            </Tag>
                        ))}
                    </div>
                )}
                {items?.length > 1 && (
                    <div
                        className={cls(
                            'hidden',
                            variant === 'testimonial' && 'md:flex gap-16',
                            variant === 'mediaLarge' && 'lg:flex gap-8',
                        )}>
                        <Button
                            component="button"
                            variant="secondary"
                            size="icon_large"
                            leftIcon="arrow-direction--type-left"
                            onClick={onPrevButtonClick}
                            disabled={prevBtnDisabled}
                        />
                        <Button
                            component="button"
                            onClick={onNextButtonClick}
                            disabled={nextBtnDisabled}
                            variant="secondary"
                            size="icon_large"
                            leftIcon="arrow-direction--type-right"
                        />
                    </div>
                )}
            </div>

            <div ref={emblaRef} className="container mt-xl">
                <div className="flex">
                    {items?.length > 0 &&
                        items.map((item, i) => {
                            return (
                                <div
                                    key={`carousel-slide-${i}-${variant}`}
                                    className={cls(
                                        'flex-[0_0_100%] lg:flex-[0_0_90%] xl:flex-[0_0_80%] card-wrapper [&+.card-wrapper]:ml-gutter [&>div]:h-full',
                                    )}>
                                    <DynamicCard {...item} cardType={variant} />
                                </div>
                            );
                        })}
                </div>
            </div>
        </section>
    );
}
